/**
 * TanStack Table column definitions for the product table.
 */
import { createColumnHelper } from '@tanstack/react-table'
import { Badge } from '@/components/v2/ui/Badge'
import { Sparkline } from '@/components/v2/ui/Sparkline'
import type { Product } from '@/types/products'

const col = createColumnHelper<Product>()
const eur = (v: number | null | undefined) =>
  v != null ? v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '\u2014'

export const productColumns = [
  col.display({
    id: 'image',
    header: '',
    cell: ({ row }) => {
      const url = row.original.first_image_url ?? row.original.image_url
      return url
        ? <img src={url} alt="" className="h-8 w-8 rounded object-cover" />
        : <div className="h-8 w-8 rounded bg-neutral-100 dark:bg-neutral-800" />
    },
    enableSorting: false,
    size: 56,
  }),
  col.accessor('name', {
    header: 'Name',
    cell: ({ row }) => (
      <div className="min-w-[160px]">
        <span className="font-medium line-clamp-1">{row.original.name}</span>
        {row.original.short_description && (
          <span className="block text-xs text-neutral-500 line-clamp-1">{row.original.short_description}</span>
        )}
      </div>
    ),
  }),
  col.accessor('sku', {
    header: 'SKU',
    cell: ({ getValue }) => {
      const v = getValue()
      return v ? <code className="text-xs">{v}</code> : <span className="text-neutral-400">&mdash;</span>
    },
  }),
  col.accessor('gtin', {
    header: 'GTIN',
    cell: ({ getValue }) => {
      const v = getValue()
      return v ? <code className="text-xs">{v}</code> : <span className="text-neutral-400">&mdash;</span>
    },
  }),
  col.accessor('price_net', {
    header: 'Preis (netto)',
    cell: ({ getValue }) => <span className="tabular-nums">{eur(getValue())}</span>,
  }),
  col.accessor('is_active', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? 'success' : 'secondary'} size="sm">
        {getValue() ? 'Aktiv' : 'Inaktiv'}
      </Badge>
    ),
  }),
  col.accessor('quantity_30d', {
    header: 'Absatz 30T',
    cell: ({ getValue }) => <span className="tabular-nums">{getValue() ?? '\u2014'}</span>,
  }),
  col.accessor('revenue_30d', {
    header: 'Umsatz 30T',
    cell: ({ getValue }) => <span className="tabular-nums">{eur(getValue())}</span>,
  }),
  col.accessor('quantity_90d', {
    header: 'Absatz 90T',
    cell: ({ getValue }) => <span className="tabular-nums">{getValue() ?? '\u2014'}</span>,
  }),
  col.accessor('revenue_90d', {
    header: 'Umsatz 90T',
    cell: ({ getValue }) => <span className="tabular-nums">{eur(getValue())}</span>,
  }),
  col.display({
    id: 'performance_30d',
    header: 'Performance 30T',
    enableSorting: false,
    cell: ({ row, table }) => {
      const meta = table.options.meta as
        | { seriesByProduct?: Record<string, { sale_date: string; revenue: number }[]> }
        | undefined
      const pts = (meta?.seriesByProduct?.[row.original.id] ?? []).map((d) => ({
        x: d.sale_date,
        y: d.revenue,
      }))
      return (
        <div className="flex items-center gap-2">
          <span className="tabular-nums">
            {(row.original.revenue_30d ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
          </span>
          <Sparkline data={pts} />
        </div>
      )
    },
  }),
  col.display({
    id: 'quality_score',
    header: 'Quality Score',
    enableSorting: true,
    cell: ({ row }) => {
      // readiness_score wird zur Laufzeit via ProductWithReadiness injiziert; der
      // column helper ist über das Basis-Product typisiert → Doppelcast nötig.
      const s = (row.original as unknown as { readiness_score?: number }).readiness_score
      if (s == null) return <span className="text-neutral-400">–</span>
      const color = s >= 70 ? 'text-emerald-600' : s >= 40 ? 'text-amber-500' : 'text-red-600'
      return <span className={`font-semibold ${color}`}>{s}</span>
    },
  }),
  col.display({
    id: 'actions',
    header: '',
    cell: () => null,
    enableSorting: false,
    size: 48,
  }),
]
